import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Film, User, Music, Tv } from "lucide-react";
import { MOVIES, PEOPLE, SONGS, getPerson } from "../mock/data";
import { SERIES } from "../mock/tv";
import { useTranslate } from "../context/TranslateContext";

export const SearchOverlay = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslate();
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 100); else setQ(""); }, [open]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return { movies: MOVIES.slice(0, 3), series: SERIES.slice(0, 3), people: PEOPLE.slice(0, 2), songs: SONGS.slice(0, 2) };
    const movies = MOVIES.filter((m) => `${m.title} ${m.language} ${m.genre}`.toLowerCase().includes(s));
    const series = SERIES.filter((se) => `${se.title} ${se.altTitles.join(" ")} ${se.language} ${se.category} ${se.format}`.toLowerCase().includes(s));
    const people = PEOPLE.filter((p) => `${p.name} ${p.role} ${p.langs.join(" ")}`.toLowerCase().includes(s));
    const songs = SONGS.filter((so) => `${so.title} ${so.language} ${so.genre} ${getPerson(so.singer)?.name || ""}`.toLowerCase().includes(s));
    return { movies, series, people, songs };
  }, [q]);

  const go = (path) => { onClose(); navigate(path); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose} data-testid="search-overlay"
          className="fixed inset-0 z-[90] flex justify-center bg-ink/85 p-4 pt-24 backdrop-blur-md">
          <motion.div initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -30, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()}
            className="h-fit max-h-[75vh] w-full max-w-2xl overflow-hidden rounded-2xl border border-edge bg-surface">
            <div className="flex items-center gap-3 border-b border-edge px-5 py-4">
              <Search className="h-5 w-5 text-gold" />
              <input ref={inputRef} data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={t("Search movies, people, songs — in any language")}
                className="flex-1 bg-transparent font-body text-lg text-cream placeholder:text-ash focus:outline-none" />
              <button onClick={onClose} className="text-ash hover:text-cream"><X className="h-5 w-5" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-3">
              <Group icon={Film} label="Movies" show={results.movies.length}>
                {results.movies.map((m) => (
                  <Row key={m.id} testId={`search-movie-${m.id}`} onClick={() => go(`/movie/${m.id}`)}
                    thumb={m.image} title={m.title} sub={`${m.language} · ${m.year}`} />
                ))}
              </Group>
              <Group icon={Tv} label="Series & Shows" show={results.series.length}>
                {results.series.map((se) => (
                  <Row key={se.slug} testId={`search-series-${se.slug}`} onClick={() => go(`/series/${se.slug}`)}
                    thumb={se.poster} title={se.title} sub={`${se.language} · ${t(se.category)}`} />
                ))}
              </Group>
              <Group icon={User} label="People" show={results.people.length}>
                {results.people.map((p) => (
                  <Row key={p.id} testId={`search-person-${p.id}`} onClick={() => go(`/person/${p.id}`)}
                    thumb={p.image} round title={p.name} sub={t(p.role)} />
                ))}
              </Group>
              <Group icon={Music} label="Songs" show={results.songs.length}>
                {results.songs.map((so) => (
                  <Row key={so.id} testId={`search-song-${so.id}`} onClick={() => go(`/song/${so.id}`)}
                    title={so.title} sub={`${getPerson(so.singer)?.name || ""} · ${t(so.genre)}`} />
                ))}
              </Group>
              {!results.movies.length && !results.series.length && !results.people.length && !results.songs.length && (
                <div className="p-8 text-center font-body text-ash" data-testid="search-empty">{t("No results found.")}</div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Group = ({ icon: Icon, label, show, children }) => {
  const { t } = useTranslate();
  if (!show) return null;
  return (
    <div className="mb-2">
      <div className="flex items-center gap-2 px-3 py-2 text-[0.7rem] uppercase tracking-[0.2em] text-gold">
        <Icon className="h-3.5 w-3.5" /> {t(label)}
      </div>
      {children}
    </div>
  );
};

const Row = ({ thumb, round, title, sub, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className="flex w-full items-center gap-4 rounded-xl px-3 py-2.5 text-left transition-colors duration-200 hover:bg-black/5">
    {thumb !== undefined ? (
      <img src={thumb} alt={title} className={`h-11 w-11 shrink-0 object-cover ${round ? "rounded-full" : "rounded-md"}`} />
    ) : (
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-edge text-gold"><Music className="h-4 w-4" /></span>
    )}
    <span className="min-w-0">
      <span className="block truncate font-body text-cream">{title}</span>
      <span className="block truncate font-body text-sm text-ash">{sub}</span>
    </span>
  </button>
);
